import { isWorking } from '@/lib/personnel';
import type { Personnel, PersonnelAssignment, Site, SiteStatus, SiteTaskItem, Week } from '@/lib/types';
import { formatContractMonthsLabel } from '@/lib/utils/contractMonths';
import { getWeekLabel } from '@/lib/utils/week';
import { parseProgress } from '@/pages/meetings/siteTaskContent';

/** 현장 정보이 쓰는 표시 형식과 집계. 화면 코드에서 계산 로직을 덜어내려고 모았다. */

export const STATUS_STYLES: Record<SiteStatus, { background: string; color: string; line: string }> = {
  진행: { background: '#e0f2fe', color: '#0369a1', line: '#38bdf8' },
  평가: { background: '#fef3c7', color: '#b45309', line: '#f59e0b' },
  미진행: { background: '#f1f5f9', color: '#475569', line: '#94a3b8' },
  준공: { background: '#fdf2f8', color: '#be185d', line: '#f472b6' },
};

export const CATEGORY_STYLES: Record<string, { background: string; color: string }> = {
  국내진행현장: { background: '#dbeafe', color: '#1d4ed8' },
  국내계약현장: { background: '#dcfce7', color: '#15803d' },
  해외진행현장: { background: '#fef3c7', color: '#92400e' },
  해외검토현장: { background: '#fee2e2', color: '#b91c1c' },
};

export function isOverseas(site: Site): boolean {
  return site.siteCategory.startsWith('해외');
}

/**
 * 현장 묶음과 그 차례(사용자 지시 2026-10-15). 진행만 국내·해외로 가른다. 홈 현장 리스트 위젯과
 * 조직도의 현장 고르기(사용자 지시 2026-10-16)와 현장 현황의 탭 한 줄(사용자 지시 2026-10-19)이 같은
 * 묶음을 쓴다. `status`·`region` 은 현장 통합 그래프 서버에 그대로 실어 보내는 값이다.
 */
export const SITE_SECTIONS: Array<{
  key: string;
  label: string;
  status: SiteStatus;
  region?: 'domestic' | 'overseas';
  match: (site: Site) => boolean;
}> = [
  { key: 'domestic', label: '국내 진행', status: '진행', region: 'domestic', match: (site) => site.status === '진행' && !isOverseas(site) },
  { key: 'overseas', label: '해외 진행', status: '진행', region: 'overseas', match: (site) => site.status === '진행' && isOverseas(site) },
  { key: 'pending', label: '예정', status: '미진행', match: (site) => site.status === '미진행' },
  { key: 'evaluating', label: '평가', status: '평가', match: (site) => site.status === '평가' },
  { key: 'completed', label: '준공', status: '준공', match: (site) => site.status === '준공' },
];

export function formatNumber(value: number | null, fractionDigits = 0): string {
  if (value === null || !Number.isFinite(value)) return '-';
  return value.toLocaleString('ko-KR', { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits });
}

/** 제곱미터를 평으로. 1평 = 3.305785㎡ */
export function toPyeong(sqm: number | null): number | null {
  return sqm && sqm > 0 ? Math.round((sqm / 3.305785) * 10) / 10 : null;
}

export function formatArea(sqm: number | null): string {
  if (sqm == null) return '-';
  return `${formatNumber(sqm)}㎡ / ${formatNumber(toPyeong(sqm), 1)}평`;
}

export function formatDateRange(start: string | null, end: string | null): string {
  const parts = [start, end].filter(Boolean);
  return parts.length > 0 ? parts.join(' ~ ') : '-';
}

export function formatContractMonths(site: Site): string {
  return formatContractMonthsLabel(site.contractStartDate, site.contractEndDate) || '-';
}

/** 인쇄용 공사개요 3줄. */
export function formatSiteOverview(site: Site) {
  return {
    contractLine: `공사기간: 계약 ${formatDateRange(site.contractStartDate, site.contractEndDate)}${
      site.targetCompletionDate ? ` (목표 준공일 ${site.targetCompletionDate})` : ''
    }`,
    partnerLine: `발주처/감리/설계사: ${site.clientName || '-'} / ${site.supervisorName || '-'} / ${site.designerName || '-'}`,
    scaleLine: `규모/용도: 연면적 ${site.grossAreaSqm != null ? `${formatNumber(site.grossAreaSqm)}㎡` : '-'} / 층수 ${
      site.floorCount || '-'
    } / 시설구분 ${site.facilityType || '-'}`,
  };
}

export type SiteContacts = {
  /** 그 현장의 현장소장(담당 「현장소장」). 없으면 빈 배열 — 출력은 있는 쪽만 줄을 세운다(사용자 지시 2026-10-28). */
  siteHeads: string[];
  /** 현장소장의 이름만. */
  siteHeadNames: string[];
  /** 그 현장의 현장대리인. 계약상 대리인 칸이 켜진 사람도 함께(소장이 겸하면 소장도). */
  siteAgents: string[];
  /** 현장대리인의 이름만. 없으면 빈 배열. */
  siteAgentNames: string[];
  siteAdmins: string[];
};

/**
 * 현장별 현장소장·현장대리인·공무 담당자. 소장은 현장현황 출력이 읽는다 — 소장만 있으면 소장, 대리인만 있으면 대리인,
 * 둘 다면 둘 다(사용자 지시 2026-10-28). 「총괄소장」 은 소장이 아니다(여러 현장을 함께 보는 자리).
 *
 * 담당은 자유 입력이라 **글자에 들어 있는지**로 가른다(v1부터 그렇다). 대리인은 칸이 생겼지만
 * 그 칸이 없던 때의 줄도 있어 담당 글자를 함께 본다.
 * 배치는 현장 id 가 아니라 현장 **이름**으로 묶인다(v1 스키마).
 */
export function buildSiteContacts(
  sites: Site[],
  personnel: Personnel[],
  assignments: PersonnelAssignment[],
): Map<string, SiteContacts> {
  const activePersonnel = personnel.filter((person) => isWorking(person));
  const personById = new Map(activePersonnel.map((person) => [person.id, person]));

  const rowsBySiteName = new Map<string, Array<{ assignment: PersonnelAssignment; person: Personnel }>>();
  for (const assignment of assignments) {
    if (assignment.assignmentType !== 'site') continue;
    const person = personById.get(assignment.personnelId);
    if (!person) continue;

    rowsBySiteName.set(assignment.assignmentName, [
      ...(rowsBySiteName.get(assignment.assignmentName) ?? []),
      { assignment, person },
    ]);
  }
  rowsBySiteName.forEach((rows) => rows.sort((a, b) => a.assignment.sortOrder - b.assignment.sortOrder));

  const toLine = (person: Personnel) =>
    [person.name, person.rank].filter(Boolean).join(' ') + (person.phone ? ` (${person.phone})` : '');

  const contacts = new Map<string, SiteContacts>();
  for (const site of sites) {
    const rows = rowsBySiteName.get(site.name) ?? [];

    const titleOf = (row: { assignment: PersonnelAssignment; person: Personnel }) =>
      row.assignment.roleTitle || row.person.position || '';

    const heads = rows.filter((row) => (row.assignment.roleTitle ?? '').trim() === '현장소장');
    const agents = rows.filter((row) => row.assignment.siteAgent || titleOf(row).includes('현장대리'));
    // 조직도 공무 묶음에 선 사람은 그 자리의 [연락처 공개] 가 정한다(V418). 묶음에 없으면 담당
    // 글자로 가르고, 사무보조(담당·고용구분)는 올리지 않는다(사용자 지시 2026-10-07).
    const admins = rows.filter((row) =>
      row.assignment.contactPublic != null
        ? row.assignment.contactPublic
        : !(row.assignment.roleTitle ?? '').includes('사무보조')
      && row.assignment.employmentType !== 'OFFICE_ASSIST'
      && [row.assignment.roleTitle, row.assignment.fieldName, row.person.position, row.person.field]
        .filter(Boolean)
        .join(' ')
        .includes('공무'),
    );

    contacts.set(site.name, {
      siteHeads: heads.map((row) => toLine(row.person)),
      siteHeadNames: heads.map((row) => row.person.name),
      siteAgents: agents.length > 0 ? agents.map((row) => toLine(row.person)) : ['-'],
      siteAgentNames: agents.map((row) => row.person.name),
      siteAdmins: admins.length > 0 ? admins.map((row) => toLine(row.person)) : ['-'],
    });
  }

  return contacts;
}

function clampPercent(value: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 0;
}

export type ProgressSnapshot = {
  planPercent: number | null;
  actualPercent: number | null;
  /** 이 값이 언제 기준인지 — 주간회의면 주차 라벨, WBS 롤업이면 'WBS 롤업'. */
  weekLabel: string | null;
};

/** 현장별 WBS 가중 롤업. `PmisProgressRollup` 중 이 함수가 쓰는 것만 추린 모양이다. */
export type WbsRollup = { siteId: string | null; plannedRate: number | null; actualRate: number | null; itemCount: number };

/**
 * 현장 공정율 — <b>주간회의가 정본, WBS 롤업이 폴백</b>(2026-08-13 뒤집음).
 *
 * <h2>왜 뒤집었나</h2>
 * 전에는 WBS 롤업이 먼저였다(DOC-12 §1.3). 그런데 <b>진척의 원천을 주간회의로</b> 정하고 나서
 * (DOC-38), WBS 가 들어간 현장은 회의에 아무리 적어도 카드와 그래프가 꿈쩍하지 않았다 —
 * 매주 적는 사람 눈에는 "적어도 소용없는 화면" 이 된다(사용자 지적 2026-08-13).
 *
 * <p>그래서 <b>회의에 적힌 값이 있으면 그것</b>을 쓰고, 한 번도 안 적은 현장만 WBS 롤업으로 간다.
 * 둘 다 없으면 빈 값이다 — 0% 로 채우면 착공도 안 한 현장이 0% 로 보고된다.
 *
 * <p>회의 본문을 여기서 다시 파싱하는 것은 아직 남은 빚이다(DOC-38 §9) — 같은 값이 진척 원장
 * ({@code pmis_progress_weeks})에도 있으므로, 원장을 읽는 API 가 서면 이 파싱은 지운다.
 *
 * @param rollups 전 현장 롤업 목록(`/v1/pmis/progress-rollups`). 회의 기록이 없을 때만 쓴다
 */
export function buildProgressSnapshot(
  site: Site,
  weeks: Week[],
  taskItems: SiteTaskItem[],
  rollups?: WbsRollup[],
): ProgressSnapshot {
  const weekly = buildWeeklyProgressSnapshot(site, weeks, taskItems);
  if (weekly.planPercent != null || weekly.actualPercent != null) {
    return weekly;
  }

  const rollup = rollups?.find((row) => row.siteId === site.id);
  if (rollup && rollup.itemCount > 0) {
    return {
      planPercent: rollup.plannedRate == null ? null : clampPercent(rollup.plannedRate),
      actualPercent: rollup.actualRate == null ? null : clampPercent(rollup.actualRate),
      weekLabel: 'WBS 공정표',
    };
  }
  return weekly;
}

/**
 * 주간회의 본문에서 읽는 옛 방식(폴백). 선택한 주차보다 나중에 적힌 것은 보지 않는다.
 *
 * 주차 순서는 `endDate` 기준으로 매긴다 — 같은 주차에 여러 번 적었으면 나중 것이 이긴다.
 */
function buildWeeklyProgressSnapshot(site: Site, weeks: Week[], taskItems: SiteTaskItem[]): ProgressSnapshot {
  const orderedWeeks = [...weeks].sort((a, b) => a.startDate.localeCompare(b.startDate));
  const weekOrder = new Map(orderedWeeks.map((week, index) => [week.id, index]));

  const progressItems = taskItems
    .filter((item) => item.siteId === site.id && item.category === '공정율')
    .sort((a, b) => (weekOrder.get(a.weekId) ?? -1) - (weekOrder.get(b.weekId) ?? -1) || a.sortOrder - b.sortOrder);

  let planPercent: number | null = null;
  let actualPercent: number | null = null;
  let weekId: string | null = null;

  for (const item of progressItems) {
    const progress = parseProgress(item.content);
    const plan = Number(progress?.plan ?? '');
    const actual = Number(progress?.actual ?? '');
    if (!Number.isFinite(plan) && !Number.isFinite(actual)) continue;

    weekId = item.weekId;
    if (Number.isFinite(plan)) planPercent = clampPercent(plan);
    if (Number.isFinite(actual)) actualPercent = clampPercent(actual);
  }

  const snapshotWeek = weekId ? orderedWeeks.find((week) => week.id === weekId) ?? null : null;
  return { planPercent, actualPercent, weekLabel: snapshotWeek ? getWeekLabel(snapshotWeek) : null };
}

/**
 * 공정 지연으로 보는 문턱(%p). 예정보다 이만큼 뒤처지면 빨갛게 센다.
 *
 * <p>현장 전체 대시보드의 <b>개요 탭과 매출·원가·수금 탭</b>이 같이 쓴다. 탭마다 적어 두면
 * 한쪽 문턱만 고쳐져, 같은 화면에서 개요는 빨갛고 옆 탭은 검은 숫자가 나온다.
 */
export const BEHIND_THRESHOLD = 5;

export type ProgressAverage = {
  planPercent: number | null;
  actualPercent: number | null;
  /** 실적이 있어 평균에 든 현장 수. 「n곳 기준」으로 적어 분모를 드러낸다. */
  count: number;
};

/**
 * 전 현장 <b>평균 공정율</b> — 단순 평균(사용자 확정 2026-09-23).
 *
 * <p>현장 전체 대시보드의 두 탭이 같은 숫자를 말해야 해서 여기 모았다. 개요 탭에 인라인으로
 * 있던 셈을 꺼낸 것이고 값은 그대로다 — 한쪽만 고쳐져 「개요는 62%, 매출 탭은 58%」가 되는 일을
 * 막는 것이 이 함수의 전부다.
 *
 * <p><b>실적을 적은 현장만 분모</b>다. 안 적은 현장을 0% 로 세면 착공도 안 한 현장이 평균을
 * 끌어내리고, 매주 적는 사람에게는 「적을수록 회사 공정율이 떨어지는」 화면이 된다
 * ({@link buildProgressSnapshot} 가 빈 값을 0 으로 채우지 않는 것과 같은 이유다).
 *
 * <p>예정도 <b>같은 분모</b>로 낸다 — 실적과 예정이 서로 다른 현장 묶음에서 나오면 둘의 차이가
 * 뜻을 잃는다(그 차이로 「공정 지연」을 세우고 있다).
 *
 * <p>도급금액 가중은 쓰지 않는다. 도급금액이 자유 입력 문자열인 현장이 있어
 * ({@code ref_sites.contract_amount}) 가중하면 그 현장이 조용히 빠지고, 개요 탭의 「평균
 * 공정률」과 숫자가 갈린다.
 */
export function averageProgress(snapshots: ProgressSnapshot[]): ProgressAverage {
  const measured = snapshots.filter((snapshot) => snapshot.actualPercent != null);
  if (measured.length === 0) return { planPercent: null, actualPercent: null, count: 0 };

  const mean = (pick: (snapshot: ProgressSnapshot) => number | null) =>
    Math.round(measured.reduce((sum, snapshot) => sum + (pick(snapshot) ?? 0), 0) / measured.length);

  return { planPercent: mean((s) => s.planPercent), actualPercent: mean((s) => s.actualPercent), count: measured.length };
}

export type SiteSortKey = 'none' | 'name' | 'progress' | 'gap' | 'completion';

export type SiteSortDirection = 'asc' | 'desc';

/** 방향 라벨은 기준을 가리지 않고 하나로 쓴다. */
export const SITE_SORT_DIRECTION_LABELS: Record<SiteSortDirection, string> = {
  asc: '오름차순',
  desc: '내림차순',
};

export type SiteSortOption = {
  key: SiteSortKey;
  label: string;
  /** 기준을 처음 고를 때의 방향. 사람이 그 기준을 거는 이유 쪽으로 맞춘다. */
  defaultDirection: SiteSortDirection;
};

/**
 * 정렬 기준 목록. <b>첫 칸이 기본값</b>이라 「비어 있을 수 없다」 를 타입으로 못박는다.
 *
 * <p>그냥 배열로 두면 {@code noUncheckedIndexedAccess} 아래에서 {@code [0]} 이
 * {@code SiteSortOption | undefined} 가 되고, 화면은 있지도 않은 「기본값이 없는 경우」 를 매번
 * 다뤄야 한다. 여기 값이 하나도 없는 상태는 이 화면에서 <b>있을 수 없는 상태</b>다 —
 * 그것을 런타임 검사가 아니라 타입으로 말한다.
 */
export const SITE_SORT_OPTIONS: [SiteSortOption, ...SiteSortOption[]] = [
  { key: 'none', label: '정렬 없음', defaultDirection: 'asc' },
  { key: 'name', label: '현장 이름순', defaultDirection: 'asc' },
  { key: 'progress', label: '공정율순', defaultDirection: 'desc' },
  { key: 'gap', label: '예정대비순', defaultDirection: 'asc' },
  { key: 'completion', label: '준공일순', defaultDirection: 'asc' },
];

/**
 * 홈 현장 리스트의 정렬 기준. 공정율·예정대비는 주차·공정 자료를 따로 읽어야 해서 뺐다 —
 * 현장 목록 하나만으로 셀 수 있는 기준만 남긴다.
 */
export const SITE_LIST_SORT_OPTIONS: [SiteSortOption, ...SiteSortOption[]] = [
  SITE_SORT_OPTIONS[0],
  ...SITE_SORT_OPTIONS.filter((option) => option.key === 'name' || option.key === 'completion'),
];

/**
 * 현장 정보 카드 정렬.
 *
 * <b>값이 없는 현장은 방향과 무관하게 뒤로</b> 보낸다 — 공정율을 한 번도 안 적은 현장을 0% 로
 * 쳐서 한쪽 끝에 몰아두면 그 현장이 "0% 인 현장" 으로 읽힌다({@link buildProgressSnapshot} 가
 * 빈 값을 0 으로 채우지 않는 것과 같은 이유다). 방향을 뒤집었더니 빈 현장이 맨 앞으로 올라오는
 * 것도 곤란하다.
 *
 * <p>기준값이 같으면 이름순으로 푼다 — 이 동률 깨기는 방향을 따라 같이 뒤집는다. 그래야
 * `desc` 가 `asc` 의 정확한 역순이 되고, 방향을 오갈 때 카드가 제자리로 돌아온다.
 *
 * @param direction `asc` 는 이름 가나다·공정율 낮은 순·`실적−예정` 작은(뒤처진) 순·계약 준공일 이른 순이다
 */
export function compareSitesBy(
  sortKey: SiteSortKey,
  direction: SiteSortDirection,
  progressBySiteId: Map<string, ProgressSnapshot>,
): (a: Site, b: Site) => number {
  const sign = direction === 'desc' ? -1 : 1;
  const byName = (a: Site, b: Site) => sign * a.name.localeCompare(b.name, 'ko');

  const metric = (site: Site): number | null => {
    if (sortKey === 'completion') {
      // 계약 준공일. 날짜가 비었거나 못 읽으면 값 없음 — 방향과 무관하게 뒤로 간다.
      const time = site.contractEndDate ? Date.parse(site.contractEndDate) : NaN;
      return Number.isNaN(time) ? null : time;
    }
    const snapshot = progressBySiteId.get(site.id);
    if (!snapshot) return null;
    if (sortKey === 'progress') return snapshot.actualPercent;
    if (snapshot.actualPercent == null || snapshot.planPercent == null) return null;
    return snapshot.actualPercent - snapshot.planPercent;
  };

  if (sortKey === 'name') return byName;

  return (a, b) => {
    const left = metric(a);
    const right = metric(b);
    if (left == null && right == null) return byName(a, b);
    if (left == null) return 1;
    if (right == null) return -1;
    if (left !== right) return sign * (left - right);
    return byName(a, b);
  };
}

/** 달마다 계약기간이 걸쳐 있는 현장 수. 타임라인 아래의 집계 줄에 쓴다. */
export function buildMonthlyCounts(sites: Site[], rangeStart: Date, rangeEnd: Date) {
  const items: Array<{ key: string; label: string; count: number; monthStart: Date; monthEnd: Date }> = [];
  let cursor = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), 1);
  const lastMonth = new Date(rangeEnd.getFullYear(), rangeEnd.getMonth(), 1);

  while (cursor <= lastMonth) {
    const monthStart = new Date(cursor);
    const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);

    const count = sites.filter((site) => {
      if (!site.contractStartDate || !site.contractEndDate) return false;
      return new Date(site.contractStartDate) <= monthEnd && new Date(site.contractEndDate) >= monthStart;
    }).length;

    items.push({
      key: `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`,
      label: `${cursor.getMonth() + 1}월`,
      count,
      monthStart,
      monthEnd,
    });

    cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
  }

  return items;
}
